import southernGlazerLogo from "/img/integration/southern-glazer.svg"
import { ConnectMailboxButtons } from "@/connect_mailbox/connect_mailbox_buttons"
import { Button } from "@/components/ui/button.tsx"
import { Input } from "@/components/ui/input.tsx"
import { Label } from "@/components/ui/label.tsx"
import { useAsyncEffect } from "@/utils"
import { DateTime } from "luxon"
import { useState } from "preact/compat"
import { useLocation } from "wouter-preact"
import { use_connect_redirect_to } from "../use_connect_redirect_to"
import { CenterColumnContainer } from "@/components/LoginScreen.tsx"
import { SyncStatusDisplay } from "@/settings/integrations/sync-status-row"
import { PortalLoginSection } from "../portal_login_section"
import {
  findIntegrationByName,
  getIntegrations,
  getSouthernGlazerMailboxHints,
  IntegrationWithJobInfo,
  SgwsMailboxHint,
  upsertIntegration,
  updateIntegration,
} from "@/api/integration"

const SOUTHERN_GLAZER_NAME = "southern-glazer"
// The brand's Supplier Billing login lives on its own row: the mail sync above needs none.
const SOUTHERN_GLAZER_PORTAL_NAME = "southern-glazer-portal"
const SUPPLIER_BILLING_URL = "https://supplierbilling.southernglazers.com"
// SGWS has no login of its own, so there is no meaningful username — a stable placeholder keeps
// the upsert idempotent, the same trick Thrive, the BOL email sync and GFI use.
const STABLE_USERNAME = "default"

export function sgws_vendor_config_ready(vendor_number: string, vendor_name: string): boolean {
  return vendor_number.trim() !== "" && vendor_name.trim() !== ""
}

export function sgws_vendor_from_integration_data(data: Record<string, unknown> | undefined): {
  vendor_number: string
  vendor_name: string
} {
  return {
    vendor_number: typeof data?.vendor_number === "string" ? data.vendor_number : "",
    vendor_name: typeof data?.vendor_name === "string" ? data.vendor_name : "",
  }
}

export function sgws_mailbox_hint_caption(hint: SgwsMailboxHint | undefined): string | null {
  if (!hint || hint.remittance_count <= 0) return null
  const n = hint.remittance_count
  return hint.suggested
    ? `Receiving Southern Glazer's remittance emails (${n})`
    : `${n} remittance ${n === 1 ? "email" : "emails"}`
}

function hint_for_mailbox(hints: SgwsMailboxHint[], integration_id: string): SgwsMailboxHint | undefined {
  return hints.find((h: SgwsMailboxHint) => h.integration_id === integration_id)
}

interface ConnectedMailboxRowProps {
  mailbox: IntegrationWithJobInfo
  hint: SgwsMailboxHint | undefined
}

function ConnectedMailboxRow({ mailbox, hint }: ConnectedMailboxRowProps) {
  const updated = mailbox.session_updated_at
    ? DateTime.fromISO(mailbox.session_updated_at).toLocaleString(DateTime.DATETIME_MED)
    : ""
  const caption = sgws_mailbox_hint_caption(hint)
  const suggested = hint?.suggested === true

  return (
    <div
      class={`rounded-md px-2 py-1.5 ${suggested ? "bg-emerald-50 ring-1 ring-emerald-200" : ""}`}>
      <div class="flex justify-between gap-4 text-sm">
        <span class={`truncate ${suggested ? "font-medium text-emerald-900" : "text-gray-700"}`}>
          {mailbox.name}: {mailbox.username}
        </span>
        {updated ? <span class="text-gray-400 shrink-0">{updated}</span> : null}
      </div>
      {caption ? (
        <p class={`text-xs mt-0.5 ${suggested ? "text-emerald-700" : "text-gray-500"}`}>{caption}</p>
      ) : null}
    </div>
  )
}

interface ConnectedMailboxListProps {
  mailboxes: IntegrationWithJobInfo[]
  hints: SgwsMailboxHint[]
}

function ConnectedMailboxList({ mailboxes, hints }: ConnectedMailboxListProps) {
  const suggested = hints.find((h: SgwsMailboxHint) => h.suggested)

  return (
    <div class="border border-gray-200 rounded-lg p-4 space-y-2">
      <h3 class="font-medium text-gray-900 text-sm">Connected mailboxes</h3>
      {suggested ? (
        <p class="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md p-2">
          {suggested.username} is already receiving Southern Glazer's remittance emails. You don't need to connect
          another mailbox.
        </p>
      ) : (
        <p class="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
          None of these mailboxes have received Southern Glazer's "Electronic Remi. Advice" emails yet. Connect the
          inbox that gets those PDFs.
        </p>
      )}
      {mailboxes.map((mailbox: IntegrationWithJobInfo) => (
        <ConnectedMailboxRow key={mailbox.id} mailbox={mailbox} hint={hint_for_mailbox(hints, mailbox.id)} />
      ))}
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function ConnectSouthernGlazerPage(props: any = {}) {
  const redirectTo = use_connect_redirect_to(props.redirectTo)
  const [, setLocation] = useLocation()
  const currentPath = window.location.pathname

  const [emailIntegrations, setEmailIntegrations] = useState<IntegrationWithJobInfo[]>([])
  const [mailboxHints, setMailboxHints] = useState<SgwsMailboxHint[]>([])
  const [sgwsIntegrationId, setSgwsIntegrationId] = useState<string | null>(null)
  const [vendorNumber, setVendorNumber] = useState("")
  const [vendorName, setVendorName] = useState("")
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [portal, setPortal] = useState<IntegrationWithJobInfo | undefined>(undefined)
  const [reloads, setReloads] = useState(0)

  useAsyncEffect(async () => {
    const [res, hintsRes] = await Promise.all([getIntegrations(), getSouthernGlazerMailboxHints()])
    if (!res.ok) {
      setLoadError("Could not load integrations")
      return
    }
    const all = res.value.data
    setEmailIntegrations(all.filter((i: IntegrationWithJobInfo) => i.name === "gmail" || i.name === "microsoft"))
    setPortal(findIntegrationByName(all, SOUTHERN_GLAZER_PORTAL_NAME))
    const sgws = findIntegrationByName(all, SOUTHERN_GLAZER_NAME)
    if (sgws) {
      setSgwsIntegrationId(sgws.id)
      const existing = sgws_vendor_from_integration_data(sgws.data)
      setVendorNumber(existing.vendor_number)
      setVendorName(existing.vendor_name)
    }
    if (hintsRes.ok) {
      setMailboxHints(hintsRes.value.data)
    }
  }, [reloads])

  async function handleSave() {
    if (!sgws_vendor_config_ready(vendorNumber, vendorName)) {
      setLoadError("Vendor number and name are required so Southern Glazer's can identify the account.")
      return
    }
    setLoading(true)
    setLoadError(null)
    let id = sgwsIntegrationId
    if (!id) {
      const created = await upsertIntegration(SOUTHERN_GLAZER_NAME, STABLE_USERNAME, {})
      if (!created.ok) {
        setLoadError("Could not enable the Southern Glazer's connection")
        setLoading(false)
        return
      }
      id = created.value.data.id
      setSgwsIntegrationId(id)
    }
    const dataRes = await updateIntegration(id, {
      data: { vendor_number: vendorNumber.trim(), vendor_name: vendorName.trim() },
      enabled: true,
    })
    if (!dataRes.ok) {
      setLoadError("Could not update settings")
      setLoading(false)
      return
    }
    setLoading(false)
    setLocation(redirectTo)
  }

  const has_mailbox = emailIntegrations.length > 0

  return (
    <CenterColumnContainer>
      <div class="space-y-6 max-w-md mx-auto">
        <div class="flex items-center justify-center">
          <img src={southernGlazerLogo} alt="Southern Glazer's" class="h-16 object-contain" />
        </div>
        <h1 class="text-xl font-semibold text-gray-900 text-center">Sync Southern Glazer's deductions</h1>
        <p class="text-gray-500 text-sm text-center">
          Every Southern Glazer's payment arrives as an "Electronic Remi. Advice" email (or, for a check, a stub in the
          post) listing what was paid and what was deducted, branch by branch. We read those from the mailboxes connected
          below and turn each line into a transaction, on a daily weekday sync.
        </p>

        <div class="space-y-3">
          <p class="text-sm font-medium text-gray-800">Connect email</p>
          <ConnectMailboxButtons
            gmailLabel="Add Gmail"
            microsoftLabel="Add Microsoft 365"
          />
        </div>

        {has_mailbox ? (
          <ConnectedMailboxList mailboxes={emailIntegrations} hints={mailboxHints} />
        ) : (
          <p class="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
            No mailbox is connected yet. Southern Glazer's sends no portal feed, so this connection has nothing to read
            until the mailbox that receives their remittance advice emails is connected above.
          </p>
        )}

        <div class="space-y-3">
          <div class="space-y-1.5">
            <Label htmlFor="sgws-vendor-number">Vendor number</Label>
            <Input
              id="sgws-vendor-number"
              value={vendorNumber}
              placeholder="e.g. 0000123456"
              onInput={(e: Event) => setVendorNumber((e.target as HTMLInputElement).value)}
            />
            <p class="text-xs text-gray-500">
              The 10-digit account on the remittance subject, e.g. Electronic Remi. Advice - … 0000123456 …
            </p>
          </div>
          <div class="space-y-1.5">
            <Label htmlFor="sgws-vendor-name">Vendor name</Label>
            <Input
              id="sgws-vendor-name"
              value={vendorName}
              placeholder="Your Company Inc"
              onInput={(e: Event) => setVendorName((e.target as HTMLInputElement).value)}
            />
            <p class="text-xs text-gray-500">As Southern Glazer's names you on the remittance, e.g. Your Company Inc.</p>
          </div>
        </div>

        <section class="border border-gray-200 rounded-lg p-4 space-y-4">
          <h2 class="font-medium text-gray-900 text-sm">Supplier Billing portal login (optional)</h2>
          {portal ? (
            <SyncStatusDisplay lastSyncJob={portal.last_sync_job} integrationName={SOUTHERN_GLAZER_PORTAL_NAME} />
          ) : null}
          {/* Keyed so the form starts from the saved login once the integration has loaded. */}
          <PortalLoginSection
            key={portal?.id ?? "none"}
            integration_name={SOUTHERN_GLAZER_PORTAL_NAME}
            portal_label="Supplier Billing portal"
            username_label="User name"
            username={portal?.username}
            onSaved={() => setReloads((n: number) => n + 1)}>
            <p class="text-sm text-gray-500">
              Southern Glazer's bills its programs — samples, co-op samples, sales promotions, events — on its Supplier
              Billing portal at{" "}
              <a href={SUPPLIER_BILLING_URL} target="_blank" rel="noopener noreferrer" class="text-blue-700 hover:underline">
                supplierbilling.southernglazers.com
              </a>
              . With the login you use there saved, we read every open bill and its backup each weekday, so a billback
              that never reached your inbox is still picked up. We only read: nothing is paid, submitted or disputed on
              the portal.
            </p>
          </PortalLoginSection>
        </section>

        {loadError ? <p class="text-sm text-red-600">{loadError}</p> : null}

        <div class="flex justify-between gap-3">
          <Button type="button" variant="outline" onClick={() => setLocation(redirectTo)}>
            Back
          </Button>
          <Button
            type="button"
            onClick={() => void handleSave()}
            disabled={loading || !sgws_vendor_config_ready(vendorNumber, vendorName)}
            class="bg-gray-900 text-white">
            {loading ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </CenterColumnContainer>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function hint(partial: Partial<SgwsMailboxHint>): SgwsMailboxHint {
    return {
      integration_id: "id",
      username: "accounting@drinkdesoi.com",
      remittance_count: 0,
      latest_at: null,
      suggested: false,
      ...partial,
    }
  }

  describe("sgws_mailbox_hint_caption", () => {
    it("names the suggested mailbox as the one receiving the remittances", () => {
      expect(sgws_mailbox_hint_caption(hint({ remittance_count: 72, suggested: true }))).toBe(
        "Receiving Southern Glazer's remittance emails (72)"
      )
    })

    it("shows a quieter count on a mailbox that also has some, but isn't the main one", () => {
      expect(sgws_mailbox_hint_caption(hint({ remittance_count: 2, suggested: false }))).toBe("2 remittance emails")
      expect(sgws_mailbox_hint_caption(hint({ remittance_count: 1, suggested: false }))).toBe("1 remittance email")
    })

    it("says nothing when the mailbox hasn't received any", () => {
      expect(sgws_mailbox_hint_caption(undefined)).toBeNull()
      expect(sgws_mailbox_hint_caption(hint({ remittance_count: 0 }))).toBeNull()
    })
  })

  describe("sgws_vendor_config_ready", () => {
    it("requires both trimmed fields", () => {
      expect(sgws_vendor_config_ready("0000116091", "De Soi Inc")).toBe(true)
      expect(sgws_vendor_config_ready(" 0000116091 ", " De Soi Inc ")).toBe(true)
      expect(sgws_vendor_config_ready("", "De Soi Inc")).toBe(false)
      expect(sgws_vendor_config_ready("0000116091", "")).toBe(false)
      expect(sgws_vendor_config_ready("  ", "De Soi Inc")).toBe(false)
    })
  })

  describe("sgws_vendor_from_integration_data", () => {
    it("reads stored vendor fields and ignores missing or non-string values", () => {
      expect(sgws_vendor_from_integration_data({ vendor_number: "0000116091", vendor_name: "De Soi Inc" })).toEqual({
        vendor_number: "0000116091",
        vendor_name: "De Soi Inc",
      })
      expect(sgws_vendor_from_integration_data({})).toEqual({ vendor_number: "", vendor_name: "" })
      expect(sgws_vendor_from_integration_data(undefined)).toEqual({ vendor_number: "", vendor_name: "" })
    })
  })
}
